/**
 * TodoTaskList - Sortable task list with long-press drag reorder.
 * Uses useSortableList hook + TodoSortableItem for smooth reanimated animations.
 */

import React, { useCallback } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  FadeIn,
  type AnimatedRef,
} from 'react-native-reanimated';

import { TodoTaskItem, ITEM_TOTAL_HEIGHT, type ActionMenuAnchor } from './TodoTaskItem';
import { TodoSortableItem } from './TodoSortableItem';
import { useSortableList } from '@/hooks/use-sortable-list';
import { IconSymbol } from '@/components/ui/icon-symbol';
import { Colors } from '@/constants/theme';
import { Spacing } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { Task } from '@/types/tasks';

type TodoTaskListProps = {
  tasks: Task[];
  focusedTaskId: string | null;
  onFocusChange: (taskId: string | null) => void;
  onToggle: (taskId: string) => void;
  onReorder: (taskId: string, toIndex: number) => void;
  onActionPress: (task: Task, anchor: ActionMenuAnchor) => void;
  onToggleSubtask?: (taskId: string, subtaskId: string) => void;
  onUpdateTitle?: (taskId: string, newTitle: string) => void;
  onAddSubtask?: (taskId: string, title: string, options?: { afterSubtaskId?: string }) => Promise<string | null>;
  onUpdateSubtaskTitle?: (taskId: string, subtaskId: string, newTitle: string) => void;
  onDeleteSubtask?: (taskId: string, subtaskId: string) => void;
  scrollEnabled: boolean;
  onScrollEnabledChange: (enabled: boolean) => void;
  /** Whether drag reorder is enabled (default: true) */
  isDragEnabled?: boolean;
  highlightedTaskId?: string | null;
  /** Animated ref to the parent ScrollView, used for drag edge auto-scroll. */
  scrollRef: AnimatedRef<Animated.ScrollView>;
  /** Window Y of the scroll viewport's top edge. */
  viewportTop: number;
  /** Window Y of the scroll viewport's bottom edge. */
  viewportBottom: number;
};

export function TodoTaskList({
  tasks,
  focusedTaskId,
  onFocusChange,
  onToggle,
  onReorder,
  onActionPress,
  onToggleSubtask,
  onUpdateTitle,
  onAddSubtask,
  onUpdateSubtaskTitle,
  onDeleteSubtask,
  scrollEnabled,
  onScrollEnabledChange,
  isDragEnabled = true,
  highlightedTaskId = null,
  scrollRef,
  viewportTop,
  viewportBottom,
}: TodoTaskListProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  const getItemId = useCallback(
    (index: number) => tasks[index]?.id ?? '',
    [tasks],
  );

  // Lock the parent scroll view synchronously while a drag is in progress.
  const handleDragStateChange = useCallback(
    (isDragging: boolean) => onScrollEnabledChange(!isDragging),
    [onScrollEnabledChange],
  );

  const { activeIndex, hoverIndex, dragY, heights, createDragGesture, measureItem } =
    useSortableList({
      itemCount: tasks.length,
      fallbackHeight: ITEM_TOTAL_HEIGHT,
      onReorder,
      getItemId,
      onDragStateChange: handleDragStateChange,
      scrollRef,
      viewportTop,
      viewportBottom,
    });

  if (tasks.length === 0) {
    return <EmptyState palette={palette} />;
  }

  const renderTaskItem = (task: Task) => (
    <TodoTaskItem
      task={task}
      onToggle={onToggle}
      onActionPress={onActionPress}
      onToggleSubtask={onToggleSubtask}
      onUpdateTitle={onUpdateTitle}
      onAddSubtask={onAddSubtask}
      onUpdateSubtaskTitle={onUpdateSubtaskTitle}
      onDeleteSubtask={onDeleteSubtask}
      isFocused={focusedTaskId === task.id}
      onFocusChange={onFocusChange}
      highlight={task.id === highlightedTaskId}
    />
  );

  return (
    <View style={styles.container}>
      {tasks.map((task, index) => {
        if (!isDragEnabled) {
          // Non-draggable: no gesture, no sortable animation
          return (
            <Animated.View key={task.id} entering={FadeIn.duration(400)}>
              {renderTaskItem(task)}
            </Animated.View>
          );
        }

        // Draggable: wrap in gesture + sortable animation
        const gesture = createDragGesture(index);
        return (
          <GestureDetector key={task.id} gesture={gesture}>
            <View>
              <TodoSortableItem
                index={index}
                activeIndex={activeIndex}
                hoverIndex={hoverIndex}
                dragY={dragY}
                heights={heights}
                taskId={task.id}
                onMeasure={measureItem}
              >
                {renderTaskItem(task)}
              </TodoSortableItem>
            </View>
          </GestureDetector>
        );
      })}
    </View>
  );
}

type EmptyStateProps = {
  palette: (typeof Colors)['light'];
};

function EmptyState({ palette }: EmptyStateProps) {
  return (
    <View style={styles.emptyContainer}>
      <IconSymbol name="checklist" size={48} color={palette.textMuted} />
      <Text style={[styles.emptyTitle, { color: palette.textMuted }]}>
        No tasks yet
      </Text>
      <Text style={[styles.emptySubtitle, { color: palette.textMuted }]}>
        Add a task below to get started
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingVertical: Spacing.sm,
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 80,
    gap: Spacing.sm,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: Spacing.md,
  },
  emptySubtitle: {
    fontSize: 14,
    opacity: 0.7,
  },
});
